import * as React from 'react';

/**
 * What the AI slide-over should treat as "this": the page, and the record or
 * queue item the person is looking at. Pages publish it with `useAiContext`;
 * ⌘J opens the assistant with it attached.
 */
export interface AiContextValue {
  page: string;
  record?: { kind: string; id: string; label: string; href?: string } | null;
  queueItem?: { type: string; id: string; label: string } | null;
  /** Short suggested prompts shown in the slide-over and previews. */
  suggestions?: string[];
}

type AiContextStore = {
  context: AiContextValue | null;
  publish: (owner: symbol, value: AiContextValue | null) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Opens the slide-over with an optional prompt to prefill. */
  ask: (prompt?: string) => void;
  pendingPrompt: string | null;
  consumePrompt: () => string | null;
};

const Store = React.createContext<AiContextStore | null>(null);

export function AiContextProvider({ children }: { children: React.ReactNode }) {
  const [entries, setEntries] = React.useState<Array<{ owner: symbol; value: AiContextValue }>>([]);
  const [open, setOpen] = React.useState(false);
  const [pendingPrompt, setPendingPrompt] = React.useState<string | null>(null);
  const publish = React.useCallback((owner: symbol, value: AiContextValue | null) => {
    setEntries(current => {
      const rest = current.filter(entry => entry.owner !== owner);
      return value ? [...rest, { owner, value }] : rest;
    });
  }, []);
  const ask = React.useCallback((prompt?: string) => {
    setPendingPrompt(prompt?.trim() || null);
    setOpen(true);
  }, []);
  const consumePrompt = React.useCallback(() => {
    const prompt = pendingPrompt;
    setPendingPrompt(null);
    return prompt;
  }, [pendingPrompt]);
  const value = React.useMemo<AiContextStore>(() => ({
    context: entries.at(-1)?.value ?? null, publish, open, setOpen, ask, pendingPrompt, consumePrompt
  }), [entries, publish, open, ask, pendingPrompt, consumePrompt]);
  return <Store.Provider value={value}>{children}</Store.Provider>;
}

/** The provider's store, or null outside it (used by the panel to fall back to local state). */
export function useAiAssistantStore() {
  return React.useContext(Store);
}

/** Access the AI slide-over; safe outside the provider (no-op) for isolated tests and public pages. */
export function useAiAssistant() {
  const store = React.useContext(Store);
  return store ?? {
    context: null, publish: () => undefined, open: false, setOpen: () => undefined,
    ask: () => undefined, pendingPrompt: null, consumePrompt: () => null
  } satisfies AiContextStore;
}

/** Publishes page/record/queue context while the calling component is mounted. The latest mounted owner wins. */
export function useAiContext(value: AiContextValue | null) {
  const { publish } = useAiAssistant();
  const owner = React.useRef(Symbol('ai-context')).current;
  const serialised = value ? JSON.stringify(value) : null;
  React.useEffect(() => {
    publish(owner, serialised ? JSON.parse(serialised) as AiContextValue : null);
  }, [owner, publish, serialised]);
  React.useEffect(() => () => publish(owner, null), [owner, publish]);
}

/** Text appended to an Agent prompt so the assistant knows what "this" refers to. */
export function describeAiContext(context: AiContextValue | null): string {
  if (!context) return '';
  const parts = [`Page: ${context.page}`];
  if (context.record) parts.push(`Record: ${context.record.kind} "${context.record.label}" (id ${context.record.id})`);
  if (context.queueItem) parts.push(`Queue item: ${context.queueItem.type} "${context.queueItem.label}" (id ${context.queueItem.id})`);
  return parts.join(' · ');
}
